/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving depot batches.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialDrugShipmentDepotBatchesState {
    drugShipmentDepotBatches: Dtos.DrugShipmentDepotBatch[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface IDrugShipmentDepotBatchesState {
    drugShipmentDepotBatches: IInitialDrugShipmentDepotBatchesState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const DrugShipmentDepotBatchesInitialState: IInitialDrugShipmentDepotBatchesState = {
    drugShipmentDepotBatches: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useDrugShipmentDepotBatchesSelector: TypedUseSelectorHook<IDrugShipmentDepotBatchesState> = useSelector;

export const DrugShipmentDepotBatchesSelectors = {
    selectDrugShipmentDepotBatches: (state: IDrugShipmentDepotBatchesState) => state.drugShipmentDepotBatches.drugShipmentDepotBatches,
    selectLoadState: (state: IDrugShipmentDepotBatchesState) => state.drugShipmentDepotBatches.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class DrugShipmentDepotBatchesReducer extends ImmerReducer<IInitialDrugShipmentDepotBatchesState> {
    public load() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadByDepotId(depotId: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadByPharmacyId(pharmacyId: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadByInstCode(instCode: string) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(drugShipmentDepotBatches?: Dtos.DrugShipmentDepotBatch[]) {
        this.draftState.drugShipmentDepotBatches = drugShipmentDepotBatches ? drugShipmentDepotBatches : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.drugShipmentDepotBatches = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...DrugShipmentDepotBatchesInitialState };
    }
};

export const DrugShipmentDepotBatchesActions = createActionCreators(DrugShipmentDepotBatchesReducer);
const reducer = createReducerFunction(DrugShipmentDepotBatchesReducer, DrugShipmentDepotBatchesInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const DrugShipmentDepotBatchesApi = {
    load: () => {
        return client.get(new Dtos.GetDrugShipmentDepotBatches());
    },
    loadByPharmacyId: (pharmacyId: number) => {
        return client.get(new Dtos.GetDrugShipmentDepotBatchesByPharmacyId({
            pharmacyId
        }));
    },
    loadByInstCode: (instCode: string) => {
        return client.get(new Dtos.GetDrugShipmentDepotBatchesByInstitutionCode({
            instCode
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const DrugShipmentDepotBatchesLogic = {
    load: createLogic<IDrugShipmentDepotBatchesState, {}, undefined, string, ReturnType<typeof DrugShipmentDepotBatchesActions.load>>({
        type: DrugShipmentDepotBatchesActions.load.type,
        process: async ({ action }, dispatch, done) => {
            try {
                const response = await DrugShipmentDepotBatchesApi.load();

                dispatch(DrugShipmentDepotBatchesActions.loadSuccess(
                    response.drugShipmentDepotBatches
                ));
            }
            catch (error: any) {
                dispatch(DrugShipmentDepotBatchesActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    loadByPharmacyId: createLogic<IDrugShipmentDepotBatchesState, {}, undefined, string, ReturnType<typeof DrugShipmentDepotBatchesActions.loadByPharmacyId>>({
        type: DrugShipmentDepotBatchesActions.loadByPharmacyId.type,
        process: async ({ action }, dispatch, done) => {
            const pharmacyId = action.payload;

            try {
                const response = await DrugShipmentDepotBatchesApi.loadByPharmacyId(pharmacyId);

                dispatch(DrugShipmentDepotBatchesActions.loadSuccess(
                    response.drugShipmentDepotBatches
                ));
            }
            catch (error: any) {
                dispatch(DrugShipmentDepotBatchesActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    loadByInstCode: createLogic<IDrugShipmentDepotBatchesState, {}, undefined, string, ReturnType<typeof DrugShipmentDepotBatchesActions.loadByInstCode>>({
        type: DrugShipmentDepotBatchesActions.loadByInstCode.type,
        process: async ({ action }, dispatch, done) => {
            const instCode = action.payload;

            try {
                const response = await DrugShipmentDepotBatchesApi.loadByInstCode(instCode);

                dispatch(DrugShipmentDepotBatchesActions.loadSuccess(
                    response.drugShipmentDepotBatches
                ));
            }
            catch (error: any) {
                dispatch(DrugShipmentDepotBatchesActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    DrugShipmentDepotBatchesLogic.load,
    DrugShipmentDepotBatchesLogic.loadByPharmacyId,
    DrugShipmentDepotBatchesLogic.loadByInstCode
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useDrugShipmentDepotBatches = (): [Dtos.DrugShipmentDepotBatch[] | null, IRequestState<Dtos.ResponseStatus>, typeof DrugShipmentDepotBatchesActions] => {
    const drugShipmentDepotBatches = useSelector(DrugShipmentDepotBatchesSelectors.selectDrugShipmentDepotBatches);
    const loadState = useSelector(DrugShipmentDepotBatchesSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof DrugShipmentDepotBatchesActions>(() => {
        return bindActionCreators(DrugShipmentDepotBatchesActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(DrugShipmentDepotBatchesActions, dispatch));
    }, [dispatch, setActions]);

    return [
        drugShipmentDepotBatches,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('drugShipmentDepotBatches', reducer as any, logics as any);